var __runInitializers = (this && this.__runInitializers) || function (thisArg, initializers, value) {
    var useValue = arguments.length > 2;
    for (var i = 0; i < initializers.length; i++) {
        value = useValue ? initializers[i].call(thisArg, value) : initializers[i].call(thisArg);
    }
    return useValue ? value : void 0;
};
var __esDecorate = (this && this.__esDecorate) || function (ctor, descriptorIn, decorators, contextIn, initializers, extraInitializers) {
    function accept(f) { if (f !== void 0 && typeof f !== "function") throw new TypeError("Function expected"); return f; }
    var kind = contextIn.kind, key = kind === "getter" ? "get" : kind === "setter" ? "set" : "value";
    var target = !descriptorIn && ctor ? contextIn["static"] ? ctor : ctor.prototype : null;
    var descriptor = descriptorIn || (target ? Object.getOwnPropertyDescriptor(target, contextIn.name) : {});
    var _, done = false;
    for (var i = decorators.length - 1; i >= 0; i--) {
        var context = {};
        for (var p in contextIn) context[p] = p === "access" ? {} : contextIn[p];
        for (var p in contextIn.access) context.access[p] = contextIn.access[p];
        context.addInitializer = function (f) { if (done) throw new TypeError("Cannot add initializers after decoration has completed"); extraInitializers.push(accept(f || null)); };
        var result = (0, decorators[i])(kind === "accessor" ? { get: descriptor.get, set: descriptor.set } : descriptor[key], context);
        if (kind === "accessor") {
            if (result === void 0) continue;
            if (result === null || typeof result !== "object") throw new TypeError("Object expected");
            if (_ = accept(result.get)) descriptor.get = _;
            if (_ = accept(result.set)) descriptor.set = _;
            if (_ = accept(result.init)) initializers.unshift(_);
        }
        else if (_ = accept(result)) {
            if (kind === "field") initializers.unshift(_);
            else descriptor[key] = _;
        }
    }
    if (target) Object.defineProperty(target, contextIn.name, descriptor);
    done = true;
};
import { memoize } from 'utilium';
import { withErrno } from './error.js';
/**
 * A synchronization primitive for handling concurrent access to a resource that can have multiple readers or an exclusive writer.
 * Modeled after Linux's `rw_semaphore`
 */
export class RwLock {
    /** Release promises for every currently held or queued acquisition */
    pending = new Set();
    /** This is the *last queued* writer */
    lastWriter;
    /** The current accepted write state of the lock */
    writeState = 0;
    /** Number of currently accepted readers */
    readers = 0;
    syncReaders = 0;
    /** Actually take the lock */
    take(mode, sync, resolve) {
        if (mode == 'rw') {
            this.writeState = sync ? 2 /* WriteState.Sync */ : 1 /* WriteState.Async */;
        }
        else {
            this.readers++;
            if (sync)
                this.syncReaders++;
        }
        let released = false;
        const release = () => {
            if (released)
                return;
            released = true;
            if (mode == 'rw') {
                this.writeState = 0;
            }
            else {
                this.readers--;
                if (sync)
                    this.syncReaders--;
            }
            resolve();
        };
        release[Symbol.dispose] = release;
        return release;
    }
    /** Queue the reader or writer */
    queue(mode) {
        const { promise, resolve } = Promise.withResolvers();
        this.pending.add(promise);
        if (mode == 'rw')
            this.lastWriter = promise;
        void promise.then(() => {
            this.pending.delete(promise);
            if (this.lastWriter == promise)
                delete this.lastWriter;
        });
        return resolve;
    }
    /**
     * Acquire a lock asynchronously
     */
    async acquire(mode) {
        const allReleased = mode == 'ro' ? this.lastWriter : Promise.all(this.pending);
        const resolve = this.queue(mode);
        await allReleased;
        return this.take(mode, false, resolve);
    }
    /**
     * Acquire a lock synchronously. Does not support waiting for existing locks.
     * @throws EDEADLK for rw if there is an existing synchronous holder (it is in the current call chain)
     * @throws EAGAIN for rw if there is an existing asynchronous holder
     */
    acquireSync(mode) {
        if (this.writeState == 2 /* WriteState.Sync */ || (mode == 'rw' && this.syncReaders))
            throw withErrno('EDEADLK');
        if (this.writeState || (mode == 'rw' && this.readers))
            throw withErrno('EAGAIN');
        return this.take(mode, true, this.queue(mode));
    }
    /** Whether a synchronous lock can be acquired for the given mode */
    isAvailable(mode) {
        return (mode != 'rw' || !this.readers) && !this.writeState;
    }
    /** Whether the lock is currently free. This value may be invalid after an `await` */
    get isFree() {
        return !this.writeState && !this.readers;
    }
    /** How the lock is currently being used. This value may be invalid after an `await` */
    get mode() {
        if (this.writeState)
            return 'rw';
        if (this.readers)
            return 'ro';
        return null;
    }
}
let RwLockable = (() => {
    let _instanceExtraInitializers = [];
    let _get_lock_decorators;
    let _get_lockSync_decorators;
    return class RwLockable {
        static {
            const _metadata = typeof Symbol === "function" && Symbol.metadata ? Object.create(null) : void 0;
            _get_lock_decorators = [memoize];
            _get_lockSync_decorators = [memoize];
            __esDecorate(this, null, _get_lock_decorators, { kind: "getter", name: "lock", static: false, private: false, access: { has: obj => "lock" in obj, get: obj => obj.lock }, metadata: _metadata }, null, _instanceExtraInitializers);
            __esDecorate(this, null, _get_lockSync_decorators, { kind: "getter", name: "lockSync", static: false, private: false, access: { has: obj => "lockSync" in obj, get: obj => obj.lockSync }, metadata: _metadata }, null, _instanceExtraInitializers);
            if (_metadata) Object.defineProperty(this, Symbol.metadata, { enumerable: true, configurable: true, writable: true, value: _metadata });
        }
        _rwLock = (__runInitializers(this, _instanceExtraInitializers), new RwLock());
        get lock() {
            return this._rwLock.acquire.bind(this._rwLock);
        }
        get lockSync() {
            return this._rwLock.acquireSync.bind(this._rwLock);
        }
        get isLocked() {
            return !this._rwLock.isFree;
        }
        get lockMode() {
            return this._rwLock.mode;
        }
    };
})();
export { RwLockable };
